/**
 * BuildRunControls — run controls rendered INSIDE the canvas header strip
 * (passed as GraphCanvas `headerActions`), alongside the legend. There is
 * no separate header bar and no duplicated model name — the workspace name
 * lives in the WorkspaceBrief above.
 *
 * Shows: last-run rollup pill, plus three controls:
 *   - Test run (calls processViewerStore.executeModel). In the project
 *     phase every run is for development/testing; the audited run-of-record
 *     is produced later when the model is saved to the inventory. The run
 *     still feeds validate_model_work + the validity judge.
 *   - History dropdown (RunsHistoryPanel)
 *   - Definition of Done dropdown (DeliverableChecklist)
 *
 * The two dropdowns are simple absolute-positioned popovers; clicking
 * outside closes them.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Play, History, ListChecks, Loader2, ChevronDown, CheckCircle2, XCircle } from 'lucide-react';
import type { ModelDefinition, ModelRun } from '../../types/modeling';
import type { PollingPhase } from '../../stores/processViewerStore';
import DeliverableChecklist from './DeliverableChecklist';
import RunsHistoryPanel from './RunsHistoryPanel';

interface BuildRunControlsProps {
  projectId: string;
  authToken: string;
  userId?: string;
  model: ModelDefinition;
  lastRun: ModelRun | null;
  activeRunProgress: ModelRun | null;
  activeRunId: string | null;
  pollingPhase: PollingPhase;
  executionError?: string | null;
  onRun: () => void;
  onStop: () => void;
}

export default function BuildRunControls({
  projectId,
  authToken,
  userId,
  model,
  lastRun,
  activeRunProgress,
  activeRunId,
  pollingPhase,
  executionError,
  onRun,
  onStop,
}: BuildRunControlsProps) {
  const isRunning = pollingPhase !== 'stopped' || !!activeRunProgress;
  const containerRef = useRef<HTMLDivElement>(null);
  const [openPanel, setOpenPanel] = useState<null | 'history' | 'dod'>(null);

  // Close popover on outside click. We use mousedown so a click that
  // *starts* outside (and ends inside, on a button text node) still
  // counts as outside — matches the pattern used elsewhere in this app.
  useEffect(() => {
    if (!openPanel) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpenPanel(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openPanel]);

  const togglePanel = (id: 'history' | 'dod') =>
    setOpenPanel((cur) => (cur === id ? null : id));

  const summary = computeRunSummary(activeRunProgress, lastRun);

  return (
    <div ref={containerRef} className="relative flex items-center gap-1.5">
      <span className="font-mono text-[10px] text-fg-tertiary">v{model.version}</span>
      <RunSummaryPill summary={summary} />
      {executionError && (
        <span
          className="max-w-[320px] truncate px-2 py-1 rounded-md border border-danger bg-danger-bg text-[11px] font-semibold text-danger"
          title={executionError}
        >
          {executionError}
        </span>
      )}
      {isRunning ? (
        <button
          onClick={onStop}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-warning bg-warning-bg border border-warning/30 rounded-md hover:bg-warning hover:text-white transition-colors"
          title="Stop watching this run. The run continues on the backend; only the UI stops polling."
        >
          <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
          Running… (stop watching)
        </button>
      ) : (
        <button
          onClick={onRun}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-white bg-accent rounded-md hover:bg-accent-hover transition-colors"
          title="Run the model end-to-end for testing. Project-phase runs are for development; the audited run-of-record is produced when the model is saved to the inventory."
        >
          <Play className="w-3.5 h-3.5" strokeWidth={2} />
          Test run
        </button>
      )}

      <button
          onClick={() => togglePanel('history')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-semibold rounded-md border transition-colors ${
            openPanel === 'history'
              ? 'bg-accent-tint text-accent-hover border-accent'
              : 'bg-surface text-fg-secondary border-border-default hover:bg-bg-warm'
          }`}
          aria-expanded={openPanel === 'history'}
        >
          <History className="w-3.5 h-3.5" strokeWidth={1.5} />
          History
          <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
        </button>

        <button
          onClick={() => togglePanel('dod')}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-semibold rounded-md border transition-colors ${
            openPanel === 'dod'
              ? 'bg-accent-tint text-accent-hover border-accent'
              : 'bg-surface text-fg-secondary border-border-default hover:bg-bg-warm'
          }`}
          aria-expanded={openPanel === 'dod'}
        >
          <ListChecks className="w-3.5 h-3.5" strokeWidth={1.5} />
          Done?
          <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
        </button>

      {/* Popovers — anchored to the right edge of the controls cluster. */}
      {openPanel === 'history' && (
        <div className="absolute right-3 top-full mt-1.5 z-20">
          <RunsHistoryPanel
            projectId={projectId}
            modelId={model.model_id}
            authToken={authToken}
            userId={userId}
            activeRunId={activeRunId}
            refreshKey={lastRun?.run_id || ''}
          />
        </div>
      )}

      {openPanel === 'dod' && (
        <div className="absolute right-3 top-full mt-1.5 z-20">
          <DeliverableChecklist model={model} lastRun={lastRun} />
        </div>
      )}
    </div>
  );
}

interface RunSummary {
  state: 'never' | 'running' | 'passed' | 'failed';
  passedCount: number;
  failedCount: number;
}

function computeRunSummary(active: ModelRun | null, last: ModelRun | null): RunSummary {
  const run = active || last;
  if (!run) return { state: 'never', passedCount: 0, failedCount: 0 };

  const passed = (run.validation_results || []).filter((v) => v.passed).length;
  const failed = (run.validation_results || []).filter((v) => !v.passed).length;

  if (active || run.status === 'running') {
    return { state: 'running', passedCount: passed, failedCount: failed };
  }
  if (run.status === 'failed' || failed > 0) {
    return { state: 'failed', passedCount: passed, failedCount: failed };
  }
  return { state: 'passed', passedCount: passed, failedCount: failed };
}

function RunSummaryPill({ summary }: { summary: RunSummary }) {
  if (summary.state === 'never') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] font-semibold uppercase tracking-wider bg-bg-warm text-fg-tertiary">
        Never run
      </span>
    );
  }

  const colorClass = {
    running: 'bg-warning-bg text-warning',
    passed: 'bg-success-bg text-success',
    failed: 'bg-danger-bg text-danger',
  }[summary.state];

  const Icon = {
    running: Loader2,
    passed: CheckCircle2,
    failed: XCircle,
  }[summary.state];

  const iconClass = summary.state === 'running' ? 'animate-spin' : '';

  const label = {
    running: 'Running',
    passed: `${summary.passedCount} passed`,
    failed: `${summary.failedCount} failed${
      summary.passedCount > 0 ? ` / ${summary.passedCount} ok` : ''
    }`,
  }[summary.state];

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px] font-semibold uppercase tracking-wider ${colorClass}`}
    >
      <Icon className={`w-3 h-3 ${iconClass}`} strokeWidth={1.5} />
      <span>{label}</span>
    </span>
  );
}
